// BETWEEN PARTIES, rebuilt (UI rebuild batch 3; drawn when lib/ui-v2.ts switches batch 3 on, inside Record's page).
//
// Astra's form, the operator's ruling of 26 Oct 2026 ("simplified or closer to that version"):
//   card    From party | To party, Amount | Asset, "More" (And in return, As of, Notes), and the foot: "Review once before
//           confirming." and the one gold primary, Review transfer. Reviewed, the foot becomes the review — From → To,
//           what moves, each party's balance in the Books' words before → after — with Edit and the confirm that posts.
//           A return leg, once opened, stays in the card (never folded away while it would post).
//   aside   laptop only: the Last 10, and About this job (the explanation, the desk, when the balances were read).
//
// THE STATE MACHINE IS screens/RecordBetweenParties.tsx's, COPIED VERBATIM: the party picks (own accounts excluded,
// readState for names and balances), the shared calculator for the two amounts and the rate, the same-party and
// same-asset words, the reference minted once per entry and held across a retry (retired only when nothing can have
// landed), the repeat guard and its panel (confirm_repeat), the success read-back from what was POSTED (PostedLine,
// deduped said as "already recorded"), the figures cleared on success, one desk (dubaiotc, no to_entity). Only the
// layout changed — plus the review step Astra's spec asks for, which adds a tap and refuses nothing.
import { useState, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { api, type P2pLeg } from '../../../lib/api';
import { useTradeMath } from '../../../hooks/useTradeMath';
import { tg } from '../../../lib/telegram';
import { ASSETS, makeReference } from '../../../lib/workflow';
import { amountOk, amountProblem, rateProblem } from '../../../lib/amount';
import { isSentinel, nothingLanded, readWords } from '../../../lib/errors';
import { readState } from '../../../lib/read-state';
import { Combobox, type ComboboxOption } from '../../Combobox';
import { rosterCut, usePartySearch } from '../../../lib/roster-cut';
import { type Desk } from '../../DeskSwitch';
import { AsOfChips } from '../../AsOfChips';
import { Field } from '../../Field';
import { BalanceRows } from './BalanceRows';
import { ErrorLine } from '../../ErrorLine';
import { MoreDetails } from '../../MoreDetails';
import { RepeatPanel, useRepeatGuard } from '../../RepeatPanel';
import { StateLine, ServerLine, TAP } from '../kit';
import { Receipt, ReceiptAmount, postedAsOf } from '../Receipt';
import { type Posted } from '../../../screens/RecordBetweenParties';
import {
  partyName, partySubtitle, isStale, needsNameReview, rankForQuery, foldAsset, sameAsset, partyBoxWords,
} from '../../../lib/naming';
import { displayAsset, fmtAmount, fmtDay, shownAmount, dubaiWallToIso, backdateProblem } from '../../../lib/format';
import { AssetSelect, FormFoot, FormLayout, ReviewBlock, deskFacts } from './p2p/parts';
import { AboutFold } from './parts';
import { LastTen, LastTenLink, type LastTenProps } from './LastTen';

/** A figure and its unit as ONE value (a no-break space joins them): a sentence wraps between values, never inside one. */
const money = (a: string | number | undefined, asset: string | undefined) => `${fmtAmount(a, asset)} ${displayAsset(asset)}`;

/** The assets on offer, FOLDED (a party's box speaks the fold; the route folds whatever it is sent). */
const P2P_ASSETS: string[] = [...new Set(ASSETS.map((a) => foldAsset(a)))];

/** One party picker's worth of reads — screens/RecordBetweenParties.tsx's usePartyPick, plus `readAt` (when the balances
 *  on screen were read, for the aside; null unless they are known). */
function usePartyPick(desk: Desk, typed: string) {
  const list = usePartySearch({
    base: ['parties', desk], text: typed,
    ask: (q) => api.listParties({ entity: desk, q, with_balances: true }),
    enabled: typed.length > 0,
  });
  const rows = list.data?.parties ?? [];
  const lookup = readState(list);
  const balancesRead = readState(list, { balances: true }) === 'known';
  const flagged = lookup === 'known' && !balancesRead;
  const options: ComboboxOption[] = useMemo(() =>
    // OWN ACCOUNTS ARE NOT PARTIES HERE — a rail's party record would move a rail's balance as though it were a customer's.
    rankForQuery(rows.filter((p) => p.party_type !== 'own_account'), typed).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: [partySubtitle(p, { compact: true }), balancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [list.data, balancesRead, typed]);   // eslint-disable-line react-hooks/exhaustive-deps
  const selected = rows.find((p) => p.slug === typed && p.party_type !== 'own_account') ?? null;
  return {
    options, selected, loading: typed.length > 0 && lookup === 'waiting', error: lookup === 'failed' ? list.error : null, balancesRead, flagged,
    cut: rosterCut(list.data), refetch: list.refetch, readAt: balancesRead && list.dataUpdatedAt ? list.dataUpdatedAt : null,
  };
}

export function BetweenPartiesV2() {
  const nav = useNavigate();
  // ONE DESK (operator, 25 Sept 2026) — both sides dubaiotc; see screens/RecordBetweenParties.tsx.
  const DESK: Desk = 'dubaiotc';
  // Deep link: /record?mode=p2p&from=<slug>&to=<slug>.
  const [search] = useSearchParams();
  const [from, setFrom] = useState(search.get('from') ?? '');
  const [to, setTo]     = useState(search.get('to') ?? '');
  const [asset, setAsset]   = useState<string>('AED');
  // The return leg. Closed = one-way move; open = a swap, and the leg posts.
  const [swap, setSwap]             = useState(false);
  const [backAsset, setBackAsset]   = useState<string>('USDT');
  // BOTH AMOUNTS AND THE RATE LIVE IN THE SHARED CALCULATOR (12 Sept 2026) — no local copy of any of the three.
  const math = useTradeMath(asset, backAsset);
  const amount     = math.amountOf(asset);
  const backAmount = math.amountOf(backAsset);
  const amountAuto = swap && math.isDerived(asset);
  const backAuto   = swap && math.isDerived(backAsset);
  const rateAuto   = swap && math.rateIsDerived;
  const [notes, setNotes] = useState('');
  const [editing, setEditing] = useState<'amount' | 'back' | null>(null);

  // ── WHEN DID THIS HAPPEN? Empty means now. Dubai wall-clock, read through lib/format.ts.
  const [when, setWhen] = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  const fromPick = usePartyPick(DESK, from);
  const toPick   = usePartyPick(DESK, to);
  const fromName = fromPick.selected ? partyName(fromPick.selected) : from;
  const toName   = toPick.selected   ? partyName(toPick.selected)   : to;

  // ── what was typed, checked before it is sent ────────────────────────────
  const amountN   = Number(amount) || 0;
  const backN     = Number(backAmount) || 0;
  const amountWords = amountProblem(amount);
  const backWords   = amountProblem(backAmount);
  const rateWords = rateProblem(math.rate);
  const samePartyWords = from && to && from === to
    ? 'Pick two different parties'
    : null;
  const sameAssetSwapWords = swap && sameAsset(asset, backAsset)
    ? 'The return leg is in the same asset as the first — move the difference one way instead.'
    : null;

  // ── BOTH SIDES, BEFORE AND AFTER — per asset, in desk words.
  const fromBal = fromPick.selected?.balances ?? {};
  const toBal   = toPick.selected?.balances ?? {};
  type Line = { asset: string; now: number; next: number };
  const lines = (bal: Record<string, number>, dir: 1 | -1): Line[] => {
    const a = bal[foldAsset(asset)] ?? 0;
    const out: Line[] = [{ asset, now: a, next: a - dir * amountN }];
    if (swap && !sameAssetSwapWords) {
      const b = bal[foldAsset(backAsset)] ?? 0;
      out.push({ asset: backAsset, now: b, next: b + dir * backN });
    }
    return out;
  };
  // FROM falls by the first leg and rises by the return leg; TO the reverse.
  const fromLines = lines(fromBal, 1);
  const toLines   = lines(toBal, -1);

  // ── submit ───────────────────────────────────────────────────────────────
  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY — minted once per entry, held in a ref, cleared on
  // success, retired when the day it carries would change and the books provably hold nothing under it.
  const refRef = useRef<string | null>(null);
  const { twin: repeat, clearTwin, guard } = useRepeatGuard();
  const [posted, setPosted] = useState<Posted | null>(null);

  const post = async (confirmRepeat: boolean) => {
    refRef.current ??= makeReference('P2P', whenIso || undefined);
    const reference = refRef.current;
    const legs: P2pLeg[] = [{ asset, amount: amountN }];
    if (swap) legs.push({ asset: backAsset, amount: backN });   // the RETURN leg, to → from
    const data = await api.p2p({
      entity:    DESK,
      from_slug: from,
      to_slug:   to,
      // `to_entity` names a SECOND desk when the two parties don't share one — this screen does not offer that, so it is
      // always absent; the BFF must not be handed a redundant field equal to `entity` either way.
      to_entity: undefined,
      legs,
      reference,
      notes:          notes.trim() || undefined,
      effective_at:   whenIso || undefined,
      confirm_repeat: confirmRepeat || undefined,
    });
    return { data, reference, asOf: whenIso || null, legs, fromName, toName };
  };
  const submit = useMutation({
    mutationFn: (opts?: { confirmRepeat?: boolean }) => guard(() => post(opts?.confirmRepeat === true)),
    onSuccess: ({ data, reference, asOf, legs, fromName, toName }) => {
      clearTwin();
      refRef.current = null;
      setPosted({ reference: data.reference ?? reference, asOf, legs: data.legs ?? legs, deduped: !!data.deduped, fromName, toName });
      // The figures leave with the entry they were typed for; the parties stay.
      setWhen(''); math.reset(); setNotes('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });
  const retireReference = () => {
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) refRef.current = null;
  };

  const readError = fromPick.error ? readWords(fromPick.error) : toPick.error ? readWords(toPick.error) : null;
  const balancesUnread = fromPick.flagged || toPick.flagged;
  const retryRead = () => {
    if (fromPick.error || fromPick.flagged) void fromPick.refetch();
    if (toPick.error || toPick.flagged) void toPick.refetch();
  };
  // BOTH PARTIES PICKED FROM THE LIST, not merely typed (a slug that names nobody cannot reach the route).
  const canPost = !submit.isPending
    && !!fromPick.selected && !!toPick.selected && !samePartyWords
    && amountOk(amount)
    && (!swap || (amountOk(backAmount) && !sameAssetSwapWords))
    && !whenWords;
  // Why Review is off, in a few words (Record's rule; Screens' audit, 27 Oct 2026). Two-of-the-same and the swap's asset
  // say their own words on the form.
  const missing = !fromPick.selected ? 'Pick the From party.'
    : !toPick.selected ? 'Pick the To party.'
    : !amountOk(amount) ? 'Enter an amount.'
    : swap && !amountOk(backAmount) ? 'Enter the return amount.'
    : whenWords ? 'Check the date.'
    : null;

  // ── THE REVIEW (Astra: "Review transfer", then the confirm). It holds only for the entry it was opened on: any change
  // to a field closes it, so the read-back is always what posts. It refuses nothing — the confirm posts exactly as the
  // old button did, under the same canPost.
  const entryKey = JSON.stringify([from, to, asset, amount, swap, backAsset, backAmount, when, notes]);
  const [reviewedKey, setReviewedKey] = useState<string | null>(null);
  const reviewing = reviewedKey === entryKey;
  const asOfWords = whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : '';
  const readAt = Math.max(fromPick.readAt ?? 0, toPick.readAt ?? 0) || null;

  const partyEffect = (side: 'From' | 'To', name: string, pick: ReturnType<typeof usePartyPick>, ls: Line[]) => (
    <div className="min-w-0">
      <p className="text-label font-medium uppercase tracking-wider text-tg-hint">{side}</p>
      <p className="break-words text-body font-medium">{name}</p>
      {pick.balancesRead
        ? <BalanceRows lines={ls.map((l) => ({ asset: l.asset, now: l.now, after: amountN > 0 ? l.next : undefined }))} />
        : <StateLine tone="warn">Balances not read.</StateLine>}
    </div>
  );

  // About this job: the explanation, the desk and when the balances were read — beside the form, and under it on the phone.
  const about = (className = '') => (
    <AboutFold className={className} facts={deskFacts(readAt)} help={<>
      <p>One party’s balance moves to another’s. No desk account moves and the desk earns nothing.</p>
      <p>“And in return” adds a leg going back: a swap between the two at their own terms. Any two of the two amounts and the rate fill the third.</p>
    </>} />
  );

  const card = (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-4">
        <Field label="From party">
          <Combobox
            value={from}
            onChange={setFrom}
            options={fromPick.options}
            more={fromPick.cut}
            loading={fromPick.loading}
            placeholder="Search party"
            emptyHint={from.length === 0 ? null : fromPick.error ? 'Parties could not be read.' : undefined}
          />
        </Field>
        <Field label="To party">
          <Combobox
            value={to}
            onChange={setTo}
            options={toPick.options}
            more={toPick.cut}
            loading={toPick.loading}
            placeholder="Search party"
            emptyHint={to.length === 0 ? null : toPick.error ? 'Parties could not be read.' : undefined}
          />
        </Field>
      </div>
      <ErrorLine>{samePartyWords}</ErrorLine>
      {(readError || balancesUnread) && (
        <StateLine
          tone="warn"
          more={readError ?? undefined}
          action={<button type="button" className="btn-link text-meta" onClick={retryRead}>Retry</button>}
        >
          {readError ? 'Couldn’t read parties — try again.' : 'Balances not read — try again.'}
        </StateLine>
      )}

      <div className="grid grid-cols-2 gap-3 lg:gap-4">
        <Field label="Amount" hint={amountAuto ? 'auto' : undefined}>
          <input aria-label={`Amount in ${displayAsset(asset)}`} className={`input${amountAuto ? ' text-tg-hint' : ''}`} value={shownAmount(amount, asset, amountAuto, editing === 'amount')} onChange={(e) => math.setAmount(asset, e.target.value)} onFocus={() => setEditing('amount')} onBlur={() => setEditing(null)} inputMode="decimal" />
          <ErrorLine>{amountWords}</ErrorLine>
        </Field>
        <Field label="Asset">
          <AssetSelect label="Asset" value={asset} onChange={setAsset} options={P2P_ASSETS} />
        </Field>
      </div>

      {/* THE RETURN LEG, once opened from More: in the card, never folded away while it would post. */}
      {swap && (
        <div className="space-y-3 border-l border-stroke pl-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-meta text-tg-hint">And in return</span>
            <button type="button" className="btn-link text-meta max-lg:min-h-[44px]" onClick={() => setSwap(false)}>Remove</button>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Field label="Asset back" className="max-lg:col-span-2">
              <AssetSelect label="Asset back" value={backAsset} onChange={setBackAsset} options={P2P_ASSETS.filter((a) => !sameAsset(a, asset))} />
            </Field>
            <Field label="Amount back" hint={backAuto ? 'auto' : undefined}>
              <input aria-label={`Amount back in ${displayAsset(backAsset)}`} className={`input${backAuto ? ' text-tg-hint' : ''}`} value={shownAmount(backAmount, backAsset, backAuto, editing === 'back')} onChange={(e) => math.setAmount(backAsset, e.target.value)} onFocus={() => setEditing('back')} onBlur={() => setEditing(null)} inputMode="decimal" />
              <ErrorLine>{backWords}</ErrorLine>
              <ErrorLine>{sameAssetSwapWords}</ErrorLine>
            </Field>
            <Field label="Rate" hint={sameAssetSwapWords ? undefined : `${math.rateWords}${rateAuto ? ' · auto' : ''}`}>
              <input
                aria-label="Rate"
                className={`input${rateAuto ? ' text-tg-hint' : ''}`}
                value={math.rate}
                onChange={(e) => math.setRate(e.target.value)}
                inputMode="decimal"
                disabled={!!sameAssetSwapWords}
              />
              <ErrorLine>{rateWords}</ErrorLine>
            </Field>
          </div>
        </div>
      )}

      <div className="border-t border-stroke-soft pt-3">
        <MoreDetails label="More">
          {!swap && (
            <button type="button" className="btn-link flex items-center gap-1 text-meta max-lg:min-h-[44px]" aria-expanded={false} onClick={() => setSwap(true)}>
              <ChevronRight size={12} aria-hidden />And in return
            </button>
          )}
          {/* The reference carries the day — a new day, a new reference, unless the old one may already be in the ledger. */}
          <AsOfChips value={when} onChange={(w) => { setWhen(w); retireReference(); }} disabled={submit.isPending} />
          <Field label="Notes">
            <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
          </Field>
        </MoreDetails>
      </div>

      {reviewing ? (
        <ReviewBlock>
          <p className="text-body">
            <span className="whitespace-nowrap font-mono tabular">{money(amountN, asset)}</span> moves
            {swap && <> · <span className="whitespace-nowrap font-mono tabular">{money(backN, backAsset)}</span> back</>}
            {asOfWords}
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-start">
            {partyEffect('From', fromName, fromPick, fromLines)}
            <span className="hidden text-tg-hint sm:block" aria-hidden>→</span>
            {partyEffect('To', toName, toPick, toLines)}
          </div>
          <div className="flex flex-col gap-2 pt-2 lg:flex-row lg:justify-end">
            <button type="button" className="btn-secondary min-h-[44px] lg:min-h-[40px] lg:px-4" onClick={() => setReviewedKey(null)}>
              Edit
            </button>
            <button
              className="btn-primary w-full lg:w-auto lg:px-6"
              disabled={!canPost}
              onClick={() => submit.mutate({})}
            >
              {submit.isPending ? 'Booking…' : `${swap ? 'Record the swap' : 'Record the move'}${asOfWords}`}
            </button>
          </div>
        </ReviewBlock>
      ) : (
        <FormFoot line={missing && !submit.isPending ? missing : null}>
          <button
            className="btn-primary w-full lg:w-auto lg:px-6"
            disabled={!canPost}
            onClick={() => setReviewedKey(entryKey)}
          >
            Review transfer<ArrowRight size={16} className="ml-2 inline" aria-hidden />
          </button>
        </FormFoot>
      )}

      {submit.isError && !isSentinel(submit.error) && <ServerLine error={submit.error} />}
      {repeat && (
        <RepeatPanel
          twin={repeat}
          pending={submit.isPending || !!whenWords}
          onAlreadyBooked={() => { clearTwin(); nav(-1); }}
          // This route only ever meets repeat_suspected, so confirm_repeat is the one flag it sends.
          onPostAnyway={() => submit.mutate({ confirmRepeat: true })}
        />
      )}
      <LastTenLink {...LAST_TEN_P2P} title="Last 10 between parties" />
      {about('xl:hidden')}
    </>
  );

  // The Last 10 moves between parties (restored 1 Nov 2026); after a posting it is all the aside keeps (Astra).
  const lastTenList = <LastTen {...LAST_TEN_P2P} title="Last 10 between parties" />;
  // Beside the form only the Last 10 and the explanation (Astra, 1 Nov 2026: no "Before confirming" block — the Review
  // button says it; the review names both parties and the effect).
  const aside = (
    <div className="space-y-6">
      {lastTenList}
      {about()}
    </div>
  );

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what MOVED, from what posted. */
  const receipt = submit.isSuccess && posted ? (
    <Receipt
      title="Moved between parties"
      deduped={posted.deduped}
      reference={posted.reference}
      facts={[
        { label: 'Moved', value: <ReceiptAmount value={posted.legs[0]?.amount} asset={posted.legs[0]?.asset} /> },
        posted.legs[1] && { label: 'And in return', value: <ReceiptAmount value={posted.legs[1].amount} asset={posted.legs[1].asset} /> },
        { label: 'From', value: posted.fromName },
        { label: 'To', value: posted.toName },
        { label: 'As of', value: postedAsOf(posted.asOf, fmtDay, submit.submittedAt) },
      ]}
      another={{ label: 'Record another', onClick: () => submit.reset() }}
      movements="/movements?kind=p2p"
      links={!posted.deduped && <Link to="/movements?kind=p2p" className={TAP}>Recent entries →</Link>}
    />
  ) : null;

  return <FormLayout card={receipt ?? card} aside={receipt ? lastTenList : aside} />;
}

const LAST_TEN_P2P: LastTenProps = { kind: 'entries', movementKind: 'p2p' };
